import { ChangeEvent, FC } from 'react';
import { InitialValues } from '../schema';
import { Box, Grid, Typography } from '@mui/material';
import NumberField from '../../../../hook_form_fields/NumberField';
import { CustomTooltip } from '../../../../CustomTooltip';
import NetFundingTooltipText from '../../../../pricing-calculator/NetFundingTooltipText';
import { InfoIcon } from '../../../../loan_application/icons';
import { useAuth } from '../../../../../hooks';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { currencyFormatter } from '../../../../../utils/formatters';
import SelectField from '../../../../hook_form_fields/SelectField';
import NewBorrowerFormFieldset from '../NewBorrowerFormFieldset';
import {
  ProductFamily,
  SpecialPaymentProducts,
} from '../../../../../../../../types/api';
import { SolarEconomics } from '../NewBorrowerForm';
import { isConsultant as isConsultantCheck } from '../../../../../utils/helpers';
import {
  SPECIAL_PAYMENT_REQUEST_OPTIONS,
  INSTALLER_PROGRAM_APPROVED_STATUSES,
  TAX_CREDIT_REQUEST_OPTIONS,
} from '../../../../../constants';
import { useInstallerLoanProducts } from '../../../../../hooks/useInstallerLoanProducts';

type LoanInformationProps = {
  control: Control<InitialValues & SolarEconomics>;
  setValue: UseFormSetValue<InitialValues & SolarEconomics>;
};

export const LoanInformation: FC<LoanInformationProps> = ({
  control,
  setValue,
}) => {
  const { account } = useAuth();

  const approvedMilestoneProgram = account?.installer_programs?.find(
    (program) =>
      program.type.includes('Milestone') &&
      INSTALLER_PROGRAM_APPROVED_STATUSES.includes(program.status)
  );

  const hasApprovedMilestoneProgram = !!approvedMilestoneProgram;

  const isConsultant = isConsultantCheck(account?.type);

  const [
    loan_amount,
    loanType,
    loanTerm,
    loan_product,
    special_payment_percentage,
  ] = useWatch({
    control,
    name: [
      'application.loan_amount',
      'loanType',
      'loanTerm',
      'application.loan_product',
      'application.special_payment_percentage',
    ],
  });

  const numberLoanAmount = Number(loan_amount);

  const {
    productFamilyOptions,
    loanYearsOptions,
    interestRateOptions,
    selectedProductIsPremium,
    dealerFee,
  } = useInstallerLoanProducts({
    loanType: loanType as ProductFamily,
    loanTerm,
    selectedProductId: loan_product,
  });

  const showPremiumProductWarning =
    selectedProductIsPremium && numberLoanAmount > 1000000;

  const handleLoanTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const _loanType = e.target.value;

    if (loanType !== _loanType) {
      setValue('loanTerm', '');
      setValue('application.loan_product', '');
    }

    // If Special Payment is selected and its value is not coming from the Calculator, pre-select to 30%
    if (
      _loanType === ProductFamily.SPECIAL_PAYMENT &&
      !special_payment_percentage
    ) {
      setValue('application.special_payment_percentage', 30, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    } else if (_loanType === ProductFamily.TAX_CREDIT) {
      setValue('application.special_payment_percentage', 25, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    } // if not Special Payment nor Tax Credit or changed loan type to Standard, reset the value
    else if (
      _loanType &&
      !SpecialPaymentProducts.includes(_loanType as ProductFamily)
    ) {
      setValue('application.special_payment_percentage', undefined, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  };

  return (
    <NewBorrowerFormFieldset title={'Loan Information'}>
      <Grid container maxWidth="md" spacing={2}>
        <Grid item xs={12} md={4}>
          <SelectField
            control={control}
            name={'loanType' as const}
            fullWidth
            label={'Loan Type'}
            tooltip={
              <>
                <b>Standard Loans</b> have fixed terms and interest rates, while{' '}
                <b>Special Payment Loans</b> have lower monthly payments and a
                special payment on the 18th month allowing the borrower to
                leverage tax credits.
              </>
            }
            choices={productFamilyOptions}
            onChange={handleLoanTypeChange}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SelectField
            control={control}
            name={'loanTerm' as const}
            fullWidth
            label={'Loan Term / Amortization'}
            choices={loanYearsOptions.map((obj) => ({
              label: obj.label,
              value: obj.value,
            }))}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SelectField
            control={control}
            name={'application.loan_product'}
            fullWidth
            label={'Interest Rate'}
            //User should select loan type and term before selecting interest rate
            disabled={
              interestRateOptions.length === 0 || !loanTerm || !loanType
            }
            choices={interestRateOptions}
          />
        </Grid>
        {SpecialPaymentProducts.includes(loanType as ProductFamily) && (
          <Grid item xs={12} md={12}>
            <SelectField
              control={control}
              name={'application.special_payment_percentage' as const}
              sx={{
                width: '100%',
              }}
              containerSx={{ width: 0.32 }}
              label={'Special Payment Request'}
              choices={
                loanType === ProductFamily.TAX_CREDIT
                  ? TAX_CREDIT_REQUEST_OPTIONS
                  : SPECIAL_PAYMENT_REQUEST_OPTIONS
              }
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <NumberField
            thousandSeparator
            startAdornmentText="$"
            decimalScale={2}
            autoFocus={true}
            control={control}
            name="application.loan_amount"
            data-testid="loanAmount"
            label="Loan Amount"
            required
            warningText={
              showPremiumProductWarning ? (
                <Typography sx={{ fontSize: 12 }} color={'error'}>
                  Loan amount must be less than $1,000,000 for Scenario 4 loans
                </Typography>
              ) : undefined
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            display={'flex'}
            flexDirection="column"
            justifyContent={'space-between'}
            height={'100%'}
          >
            <Typography variant="body2" color="textSecondary" ml={4}>
              The amount you will receive from Sunstone (Net Funding){' '}
              <CustomTooltip title={<NetFundingTooltipText />}>
                <InfoIcon sx={{ fontSize: 12 }} />
              </CustomTooltip>
            </Typography>
            <Typography
              variant="body1"
              fontWeight={'bold'}
              color="textPrimary"
              ml={4}
            >
              {numberLoanAmount && loan_product
                ? account &&
                  currencyFormatter.format(
                    (numberLoanAmount * (100 - dealerFee)) / 100
                  )
                : '–'}
            </Typography>
          </Box>
        </Grid>
        {(hasApprovedMilestoneProgram || isConsultant) && (
          <Grid item xs={12} sm={6.1}>
            <SelectField
              control={control}
              name={'application.borrower_advance'}
              fullWidth
              label={'Would you like to make this loan a Milestone Loan?'}
              tooltip={
                <Typography textAlign={'left'}>
                  Selecting &quot;Yes&quot; will enable Milestone payments for
                  this loan. Please note that the designated EPC/installer will
                  need to apply and be approved for the Sunstone Milestone
                  program before the loan can be finalized. If the installer and
                  Borrower are approved, the Borrower will receive a loan
                  agreement that includes the milestones as well as the
                  Milestone Program interest rate and must sign this agreement
                  to enable the program.
                </Typography>
              }
              required
              choices={[
                {
                  value:
                    // Consultants should be saved as Yes instead of the Milestone Program
                    !isConsultant ? approvedMilestoneProgram?.type : 'Yes',
                  label: 'Yes',
                },
                { value: 'No', label: 'No' },
              ]}
            />
          </Grid>
        )}
      </Grid>
    </NewBorrowerFormFieldset>
  );
};

export default LoanInformation;
